import { useSession } from 'next-auth/react'

import type { KeyedMutator } from 'swr'
import useSWR from 'swr'

import {
	getCustomerOrders,
	getOrdersFulfillmentsAndTagsFromAdminAPI
} from '@lib/operations/customer'
import type { Order, Orders } from '@lib/types/order'
import type {
	SubscriptionBoxHistories,
	SubscriptionBoxHistoryNode
} from '@lib/types/subscription-box'
import { formatDateString } from '@lib/utils/dates'

import useCustomer from './useCustomer'

type UseBoxOrders = {
	orders: SubscriptionBoxHistories | null | undefined
	loading: boolean
	revalidate: KeyedMutator<SubscriptionBoxHistories | null>
}

function subscriptionBoxHistoryMapper(order: Order): SubscriptionBoxHistoryNode {
	return {
		createdAt: order.processedAt,
		month: order.processedAt ? formatDateString(order.processedAt, { month: 'long' }) : null,
		fulfillAt: order.processedAt,
		fulfillmentLineItemId: '',
		fulfillmentOrderId: order.id,
		customizationExpiration: order.processedAt,
		productSelections: order.lineItems
			.filter((item) => item.quantity > 0)
			.map((item, idx) =>
				Array(item.quantity).fill({
					fullSizeVariantId: null,
					imageUrl: item?.variant?.image?.url,
					postion: idx,
					productVariantReasons: [''],
					variantId: item?.variant?.id
				})
			)
			.flat(),
		selectionType: 'CUSTOMIZED_BOX',
		shippingDetail: {
			fulfillmentStatus: order.fulfillmentStatus,
			orderNumber: `#${order.orderNumber}`,
			shippedAt: '',
			deliveredAt: '',
			trackingNumber: '',
			trackingUrl: ''
		}
	}
}

export default function useBoxOrders(): UseBoxOrders {
	const { customer } = useCustomer()
	const session = useSession()

	const shopifyAccessToken = (session?.data as any)?.shopifyAccessToken as string | undefined

	const swrKey = customer?.id && shopifyAccessToken ? `/${customer.id}/box-orders` : null

	const { data, isValidating, mutate } = useSWR<SubscriptionBoxHistories | null>(
		swrKey,
		getBoxOrders
	)

	async function getBoxOrders(): Promise<SubscriptionBoxHistories | null> {
		if (customer?.id && shopifyAccessToken) {
			const [ordersFromStorefrontAPI, ordersFromAdminAPI] = await Promise.all([
				getCustomerOrders(shopifyAccessToken),
				getOrdersFulfillmentsAndTagsFromAdminAPI(customer.id)
			])

			if (ordersFromStorefrontAPI?.length) {
				const orders: Orders = []

				for (const orderFromAdminAPI of ordersFromAdminAPI) {
					if (
						// exclude orders with the following tags:
						(orderFromAdminAPI.tags?.includes('Subscription') === true ||
						orderFromAdminAPI.tags?.includes('prepaid recreate') === true) &&
						// Need to add OR "prepaid recreate" === true
						!orderFromAdminAPI.cancelledAt
						// reship tag is for replacement orders only
						// && orderFromAdminAPI.tags?.includes('reship') === true
					) {
						// merge orders from Storefront API and Admin API
						const orderFromStorefrontAPI = ordersFromStorefrontAPI.find((orderFromStorefront) =>
							orderFromStorefront.id.includes(orderFromAdminAPI.id)
						)

						if (orderFromStorefrontAPI) {
							const order = {
								...orderFromStorefrontAPI,
								...orderFromAdminAPI
							} as Order

							orders.push(order)
						}
					}
				}

				// re-sort orders due to the "migrated created at" metafield
				const sortedOrders = orders.sort((orderA, orderB) => {
					const orderATime = new Date(orderA.processedAt).getTime()
					const orderBTime = new Date(orderB.processedAt).getTime()

					if (orderATime < orderBTime) return 1
					else if (orderATime > orderBTime) return -1
					return 0
				})
				return sortedOrders.map(subscriptionBoxHistoryMapper)
			}
		}

		return null
	}

	return { orders: data, loading: isValidating || data === undefined, revalidate: mutate }
}
